import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'

import {useProductList} from '../util/product-list-context'
import {PRODUCT_LIST_TOTAL} from '../../product-list'

import {
    Skeleton,
    Button,
    Text,
    Flex,
    useBreakpointValue,
    Box,
    HStack
} from '@salesforce/retail-react-app/app/components/shared/ui'

import LinkedCategories from './linked-categories'
import GenderFilter from './gender-filter'

import {Filter2Icon} from '../../../components/custom-icons'

// Custom Components
import MetaTagsComponent from '../../../components/metatags'

// Custom utils
import {buildMetaData} from '../../../components/metatags/util/metaDataUtils'
import {isHydrated} from '../../../utils/utils'
import {REFINEMENT_GENDER_ATTRIBUTE} from '../../../constants'

/**
 * ProductListTopPanel is a React component that renders the top panel of a product list page.
 * It provides a sticky header with options to display linked categories or a gender filter,
 * and includes a button to open product refinements.
 *
 * @param {Object} props - The properties object.
 * @param {boolean} [props.isLinkedCategories=true] - Determines whether to display linked categories
 *     or a gender filter in the panel.
 * @param {boolean} [props.showTopNavigation=true] - Determines whether to display the linked
 *     categories or gender filter area.
 *
 * @returns {JSX.Element} The rendered component.
 */
const ProductListTopPanel = ({showTopNavigation = true, isLinkedCategories = true}) => {
    const {formatMessage} = useIntl()
    const {
        category,
        isProductListLoading,
        productSearchResult,
        onOpenRefinements,
        refinementsOpenRef,
        metatags
    } = useProductList()

    const linkedCategories = category?.c_linkedCategories

    /************************ Handle display of top PLP panel ****************************/
    const stickyRef = useRef(null)
    const headerHeight = useBreakpointValue({base: 64, md: 52})
    const isMobile = useBreakpointValue({base: true, lg: false})
    const [isIntersecting, setIntersecting] = useState(true)

    useEffect(() => {
        if (!stickyRef?.current) {
            return
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                const onScreen = entry.isIntersecting
                setIntersecting(onScreen)
            },
            {
                rootMargin: `-${
                    headerHeight + (stickyRef?.current?.clientHeight || 0) + 2
                }px 0px 0px 0px`,
                threshold: 0
            }
        )

        observer.observe(stickyRef?.current)

        return () => {
            observer.disconnect()
        }
    }, [headerHeight, stickyRef?.current])

    /*
     * Metadata constructed in the getProps method and passed to the MetaTags component.
     */
    const finalMetadata = buildMetaData(category, metatags)

    if (!category) {
        return (
            <Box px={[10, 10, 20, 20]} py={[10, 10, 14, 14]}>
                <Skeleton height="40px" width="100%" />
            </Box>
        )
    }

    return (
        <Flex
            ref={stickyRef}
            direction={{base: 'column', lg: 'row'}}
            justify="space-between"
            align="flex-start"
            gap={{base: 0, lg: 10}}
            borderTopWidth={[0, null, null, !stickyRef?.current || isIntersecting ? 0 : 1]}
            borderBottomWidth={!stickyRef?.current || isIntersecting ? 0 : 1}
            mb={!stickyRef?.current || isIntersecting ? 0 : -1}
            borderColor="borderGray"
            position="sticky"
            top={[
                `${headerHeight - (showTopNavigation && linkedCategories ? 72 : 0)}px`,
                null,
                null,
                `${headerHeight}px`
            ]}
            bg="white"
            zIndex={1}
        >
            <MetaTagsComponent metaData={finalMetadata} />

            {/* Linked categories List OR Gender Filter */}
            {showTopNavigation &&
                (isHydrated() && stickyRef?.current ? (
                    (isMobile || isIntersecting) && (
                        <Box w="full">
                            {isLinkedCategories ? (
                                <LinkedCategories categories={linkedCategories} />
                            ) : (
                                <GenderFilter />
                            )}
                        </Box>
                    )
                ) : (
                    <Box
                        px={[10, 10, 20, 20]}
                        py={[10, 10, 14, 14]}
                        width={['auto', null, '40%']}
                        maxWidth={'100vw'}
                    >
                        {isLinkedCategories && linkedCategories?.length > 0 ? (
                            <HStack gap={10}>
                                {linkedCategories.map((category) => (
                                    <Skeleton key={category.id} height="40px" width="200px" />
                                ))}
                            </HStack>
                        ) : (
                            productSearchResult?.refinements?.find(
                                (item) => item.attributeId === REFINEMENT_GENDER_ATTRIBUTE
                            ) && <Skeleton height="40px" width="100%" />
                        )}
                    </Box>
                ))}

            <Flex
                px={{base: 10, md: 20}}
                py={{base: 10, lg: isIntersecting ? 14 : 6}}
                justifyContent={{
                    base: !productSearchResult ? 'flex-end' : 'space-between',
                    lg: 'space-between'
                }}
                align="center"
                borderTopWidth={[1, null, null, 0]}
                borderColor="borderGray"
                gap={20}
                w={{base: 'full', lg: stickyRef?.current && !isIntersecting ? 'full' : 'auto'}}
                ml={{lg: showTopNavigation ? 0 : 'auto'}}
            >
                {/* Total */}
                {isProductListLoading ? (
                    <Skeleton height="20px" width="80px" />
                ) : (
                    productSearchResult && (
                        <Text variant="bodySmall" color="darkGray" whiteSpace="nowrap">
                            {formatMessage(PRODUCT_LIST_TOTAL, {
                                total: productSearchResult?.total || 0
                            })}
                        </Text>
                    )
                )}

                <Button
                    variant="primary"
                    height={['40px', null, null, isIntersecting ? '44px' : '40px']}
                    rightIcon={<Filter2Icon boxSize={4} />}
                    onClick={onOpenRefinements}
                    ref={refinementsOpenRef}
                >
                    <FormattedMessage defaultMessage="Filter" id="product_list.button.filter" />
                </Button>
            </Flex>
        </Flex>
    )
}

ProductListTopPanel.propTypes = {
    showTopNavigation: PropTypes.bool,
    isLinkedCategories: PropTypes.bool
}

export default ProductListTopPanel
